import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from '~/utils/navigation';
import { getUrlParams } from '@comeon/utility';
import { BANKID_MODES } from './configs';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';
import useBankID from './use-bankid.js';
import BankIDLoader from './loader/bankid-loader-new.js';
import { authenticationSelector } from '../ducks/selectors.js';
import { clearOrderRefCookie, isSessionCriteriaFulfilled, retrieveOrderRef, setAnalyticsCookies } from './utils.js';
import { ROUTE_GUEST_DEPOSIT_LOGIN, ROUTE_LOGIN } from '~/components/router/constants.js';
import { clearBankID } from './ducks/actions';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import BinaryQuestion from '~/components/binary-question';
import { isAndroidChrome } from '~/utils/user-agent-utils';
import useTranslations from '~/components/translation/use-translations.ts';

const TEXT_KEYS = ['MOBILE_BANKID_TITLE', 'MOBILE_BANKID_DESCRIPTION', 'BUTTON_OK', 'BUTTON_CANCEL'];

const BankIDRedirect = ({ mode = BANKID_MODES.SWISH, onClose, isStartPage = false }) => {
	const { translations: tr, isLoadingTranslations } = useTranslations(TEXT_KEYS);
	const [bankidLoading, setLoading] = useState({
		status: 'none'
	});
	const closeCurrentContext = useCloseCurrentContext();
	const bankIDModeURL = getUrlParams('mode');
	const { startBankID, bankIDTransaction, cancelBankId } = useBankID({
		mode: bankIDModeURL ?? mode
	});
	const { sessionCriteria } = useSelector(state => state.bankId);
	const isAuthenticated = useSelector(authenticationSelector);
	const router = useRouter();
	const orderRefCached = retrieveOrderRef();
	const { orderRef } = useSelector(state => state.bankId);
	const dispatch = useDispatch();
	const smaller = useSelector(isSmallerSelector);
	const androidChrome = useMemo(() => {
		return isAndroidChrome();
	}, []);

	// use mode to set cookies for gtm events
	useEffect(() => {
		setAnalyticsCookies(mode);
	}, [mode]);

	// hook for bankid loader
	useEffect(() => {
		if (bankIDTransaction.status === 'start') {
			setLoading(loading => ({
				...loading,
				...{ status: 'start' }
			}));
		} else if (bankIDTransaction.status === 'restart') {
			setLoading(loading => ({
				...loading,
				...{ status: 'pending' }
			}));
		}
	}, [bankIDTransaction.status]);

	// start bankid / continue pending bankid from cached order ref
	useEffect(() => {
		if (orderRef || bankIDTransaction.status !== 'none' || (androidChrome && isStartPage)) {
			return;
		}
		const clearOrderRefCache = () => {
			clearOrderRefCookie();
		};
		if (!orderRefCached) {
			startBankID();
		} else {
			const payload = {
				orderRef: orderRefCached
			};
			startBankID(payload);
			clearOrderRefCache();
		}
	}, [router, startBankID, orderRefCached, orderRef, bankIDTransaction.status, androidChrome, isStartPage]);

	// on cancel bankid
	const onCancel = useCallback(async () => {
		setLoading(loading => ({
			...loading,
			...{ show: true, status: 'canceling' }
		}));
		await cancelBankId(orderRef);
		if (onClose) {
			// in case of reopenable popup, we need bankid to be mounted
			const isReopenable = bankIDTransaction?.response?.isOpenable;
			if (!isReopenable) onClose();
		} else {
			router.push(ROUTE_LOGIN);
		}
		dispatch(clearBankID());
	}, [dispatch, onClose, router, bankIDTransaction, orderRef, cancelBankId]);

	const handleStartBankID = useCallback(() => startBankID(), [startBankID]);

	const handleClose = useCallback(() => closeCurrentContext(), [closeCurrentContext]);

	// on bankid failure
	useEffect(() => {
		if (bankIDTransaction.status === 'failure') {
			onCancel();
		}
	}, [bankIDTransaction, onCancel]);

	// for swish mode from url, if amount is missing
	useEffect(() => {
		const amount = getUrlParams('amount');
		if (bankIDModeURL === BANKID_MODES.SWISH && !amount) {
			onCancel();
		}
	}, [bankIDModeURL, onCancel]);

	// handle bankid success, for swish  redirect to swish flow in sidebar
	useEffect(() => {
		const redirectToSwishFlow = () => {
			const amount = getUrlParams('amount');
			const methodId = getUrlParams('methodId');
			const params = {
				showMethods: true,
				methodId,
				amount // the amount entered by user
			};
			const queryParams = new URLSearchParams(params).toString();
			const url = `${ROUTE_GUEST_DEPOSIT_LOGIN}?${queryParams}`;
			router.push(url);
		};
		if (sessionCriteria) {
			const criteria = {
				...sessionCriteria,
				isEmailSet: !sessionCriteria.shouldShowEmail,
				isMobileNumberSet: !sessionCriteria.shouldShowPhone
			};
			// in case session criteria is fulfilled wait till session is set
			if (isSessionCriteriaFulfilled(criteria) && !isAuthenticated) {
				return;
			}
			// only handled for swish now
			if (mode === BANKID_MODES.SWISH) {
				redirectToSwishFlow();
			}
		}
	}, [isAuthenticated, router, sessionCriteria, mode]);

	// this component is only develop for mobile (autostart w/o personal number)
	if (!smaller) {
		closeCurrentContext();
	}

	const bankIDFailed = bankIDTransaction.status === 'failure';

	// when coming from start page, show this prompt on android chrome to force user interaction
	const showBankIDPrompt = bankIDTransaction.status === 'none' && androidChrome && !!isStartPage;

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}

	return !bankIDFailed ? (
		<div>
			<BankIDLoader onCancel={onCancel} status={bankidLoading.status} />
			<BinaryQuestion
				shouldOpenBinary={showBankIDPrompt}
				title={tr.MOBILE_BANKID_TITLE}
				text={tr.MOBILE_BANKID_DESCRIPTION}
				onYesClick={handleStartBankID}
				onNoClick={handleClose}
				yesText={tr.BUTTON_OK}
				noText={tr.BUTTON_CANCEL}
				fixedPosition
			/>
		</div>
	) : null;
};

export default BankIDRedirect;
